<script setup lang="ts">import { computed as _computed, ref as _ref } from 'vue';

import { initials } from '@shared/string'
import { faPen } from '@fortawesome/pro-regular-svg-icons'
import { isMobile } from '@consumer/utils/breakpoints'
import { ACCENT_COLORS, DEFAULT_BACKGROUND_COLOR } from '@consumer/constants/contactPhoto'

withDefaults(defineProps<{
  name?: string
  profilePictureUrl?: string | null
  backgroundColor?: string
  size?: number
  editable?: boolean
  decorated?: boolean
}>(), { backgroundColor: DEFAULT_BACKGROUND_COLOR,size: 96,editable: false,decorated: true, })

const userInitials = _computed(() => initials(__props.name))
const textColor = _computed(() => ACCENT_COLORS[__props.backgroundColor] || ACCENT_COLORS[DEFAULT_BACKGROUND_COLOR])
let imageLoaded = _ref(false)
const hasImage = _computed(() => !!__props.profilePictureUrl)
// Small decorative dots only show when decorated and no image
const showDots = _computed(() => __props.decorated && (!hasImage.value || !imageLoaded.value))

// Reset imageLoaded when URL changes
watch(() => __props.profilePictureUrl, () => {
  imageLoaded.value = false
})

// Decorated avatars use a larger container to accommodate decorative dots
const containerSize = _computed(() => __props.decorated ? Math.round(__props.size * 1.4) : __props.size)
const center = _computed(() => containerSize.value / 2)
const avatarRadius = _computed(() => __props.size / 2)

// Dashed stroke scales proportionally with avatar size
const dashLength = _computed(() => Math.round(7 * (avatarRadius.value / 62.5)))

// 5 decorative circles positioned relative to avatar center
// Offsets are multiples of avatarRadius, derived from the Figma SVG
const DECO_CIRCLES = [
  { dx: -1.058, dy: -0.756, r: 0.101 }, // top-left
  { dx: 1.109, dy: 0.580, r: 0.076 }, // bottom-right
  { dx: -1.071, dy: 0.731, r: 0.126 }, // bottom-left
  { dx: 1.260, dy: -0.428, r: 0.126 }, // right
  { dx: 1.021, dy: -0.869, r: 0.088 }, // top-right
]

const decoCircles = _computed(() =>
  DECO_CIRCLES.map(c => ({
    cx: center.value + c.dx * avatarRadius.value,
    cy: center.value + c.dy * avatarRadius.value,
    r: c.r * avatarRadius.value,
  })),
)
</script>

<template>
  <div
    class="contact-photo no-screenshot relative inline-flex items-center justify-center"
    :style="{ width: `${containerSize}px`, height: `${containerSize}px` }"
  >
    <!-- SVG: main avatar circle + decorative dashed border + dots (when decorated) -->
    <svg
      :width="containerSize"
      :height="containerSize"
      :viewBox="`0 0 ${containerSize} ${containerSize}`"
      class="absolute inset-0"
      fill="none"
    >
      <!-- Avatar circle. Dashed border only renders for the decorated variant. -->
      <circle
        :cx="center"
        :cy="center"
        :r="avatarRadius"
        :fill="backgroundColor"
        :stroke="decorated ? 'var(--color-border-primary)' : 'none'"
        :stroke-dasharray="decorated ? `${dashLength} ${dashLength}` : undefined"
      />
      <!-- 5 scattered decorative circles -->
      <circle
        v-for="(c, i) in showDots ? decoCircles : []"
        :key="i"
        :cx="c.cx"
        :cy="c.cy"
        :r="c.r"
        :fill="backgroundColor"
      />
    </svg>

    <!-- Initials / image overlay (centered on the avatar circle) -->
    <div
      class="rounded-full overflow-hidden flex items-center justify-center font-semibold absolute"
      :style="{
        width: `${size}px`,
        height: `${size}px`,
        color: textColor,
        fontSize: `${size / 2.5}px`,
      }"
    >
      <img
        v-if="hasImage"
        v-show="imageLoaded"
        :src="profilePictureUrl!"
        :alt="name ? `Avatar for ${name}` : 'Contact avatar'"
        class="w-full h-full object-cover"
        @load="imageLoaded = true"
        @error="imageLoaded = false"
      >
      <span v-if="!hasImage || !imageLoaded" class="uppercase select-none">
        {{ userInitials }}
      </span>
    </div>

    <!-- Edit overlay -->
    <button
      v-if="editable"
      type="button"
      class="contact-photo-edit absolute inset-0 flex items-center justify-center rounded-full cursor-pointer"
    >
      <div
        class="rounded-full bg-black/40 flex items-center justify-center transition-opacity"
        :class="isMobile ? 'opacity-100' : 'opacity-0'"
        :style="{ width: `${size}px`, height: `${size}px` }"
      >
        <GIcon :icon="faPen" class="text-white" :style="{ fontSize: `${size / 5}px` }"/>
      </div>
    </button>
  </div>
</template>

<style>
.contact-photo:hover .contact-photo-edit > div {
  opacity: 1;
}
</style>
